/**
 * Teammate Code service — client-side wrapper around `/api/code/*` (spec 083).
 *
 * Powers the Coding surface: the session list, session creation and polling,
 * prompting, and the diff pane. Provisioning takes tens of seconds, so creation
 * returns immediately and the caller polls `getSession` until the state settles.
 */

import { api } from "@/utils/api-client"

/** Lifecycle states a coding session moves through (spec 083 §7.1). */
export type CodeSessionState =
  | "created"
  | "provisioning"
  | "ready"
  | "running"
  | "stopping"
  | "stopped"
  | "archived"
  | "failed"

/** States in which the composer accepts input; everything else is refused server-side. */
export function acceptsPrompt(state: CodeSessionState): boolean {
  return state === "ready"
}

/** True while the session is still coming up, so the UI can show progress. */
export function isProvisioning(state: CodeSessionState): boolean {
  return state === "created" || state === "provisioning"
}

export interface CodeSessionSummary {
  id: string
  chatId: string
  /**
   * The conversation's title, generated from the first instruction exactly as
   * every other chat's is. Falls back to the placeholder until generation
   * lands, which is a second or two after the first prompt.
   */
  title: string
  repoOwner: string
  repoName: string
  workingBranch: string
  state: CodeSessionState
  lastActivityAt: string | null
  createdAt: string
}

export interface CodeSessionDetail extends Omit<CodeSessionSummary, "lastActivityAt" | "createdAt"> {
  lastError: string | null
}

export interface CodeDiffFile {
  path: string
  previousPath: string | null
  status: "added" | "modified" | "deleted" | "renamed" | "binary"
  additions: number
  deletions: number
  patch: string | null
  truncated: boolean
}

export interface CodeDiff {
  baseSha: string
  headSha: string | null
  additions: number
  deletions: number
  files: CodeDiffFile[]
}

export interface CreateCodeSessionInput {
  repoOwner: string
  repoName: string
  baseBranch?: string
  title?: string
  /** App the session belongs to; the coding surface is app-scoped. */
  appId?: string
}

/**
 * Lists coding sessions, newest first.
 *
 * `appId` narrows the list to one app. Omitting it returns every session in the
 * workspace, which is what the reconcile tooling wants but not what the app
 * surface should show — pass the route's app id there.
 */
export async function listCodeSessions(appId?: string): Promise<CodeSessionSummary[]> {
  const path = appId === undefined ? "/code/sessions" : `/code/sessions?appId=${encodeURIComponent(appId)}`
  const response = (await api.get(path)) as { data?: CodeSessionSummary[] }
  return response.data ?? []
}

/**
 * Creates a session and starts provisioning its sandbox.
 *
 * Returns as soon as the rows exist — the sandbox is still booting, so the
 * caller polls `getSession` rather than treating this as ready.
 */
export async function createCodeSession(
  input: CreateCodeSessionInput,
): Promise<{ sessionId: string; chatId: string }> {
  const response = (await api.post("/code/sessions", input)) as {
    data: { sessionId: string; chatId: string }
  }
  return response.data
}

export async function getCodeSession(sessionId: string): Promise<CodeSessionDetail> {
  const response = (await api.get(`/code/sessions/${sessionId}`)) as { data: CodeSessionDetail }
  return response.data
}

/** Queues a prompt. Rejects when the sandbox is busy or not yet ready. */
export async function promptCodeSession(
  sessionId: string,
  content: string,
): Promise<{ messageId: string }> {
  const response = (await api.post(`/code/sessions/${sessionId}/prompt`, { content })) as {
    data: { messageId: string }
  }
  return response.data
}

export async function abortCodeSession(sessionId: string): Promise<void> {
  await api.post(`/code/sessions/${sessionId}/abort`, {})
}

/** Diff of the working tree against the session's base commit. */
export async function getCodeDiff(sessionId: string): Promise<CodeDiff> {
  const response = (await api.get(`/code/sessions/${sessionId}/diff`)) as { data: CodeDiff }
  return response.data
}

/**
 * Brings a stopped session's sandbox back up without sending a prompt.
 *
 * The diff is read from the running sandbox, so viewing what the agent changed
 * needs the container alive. Resolves once it is ready — tens of seconds for a
 * wake, longer if it has to be rebuilt.
 */
export async function wakeCodeSession(sessionId: string): Promise<void> {
  await api.post(`/code/sessions/${sessionId}/wake`, {})
}

/**
 * Stops the sandbox now rather than waiting for the idle reaper.
 *
 * Nothing is lost that a wake does not bring back: the volume survives a stop,
 * so the checkout and any uncommitted work are still there. Refused server-side
 * while a turn is running.
 */
export async function stopCodeSession(sessionId: string): Promise<void> {
  await api.post(`/code/sessions/${sessionId}/stop`, {})
}

/** True while the sandbox can answer a diff request. */
export function isSandboxLive(state: CodeSessionState): boolean {
  return state === "ready" || state === "running"
}

/**
 * True when there is a sandbox worth stopping, and stopping it is safe.
 *
 * Deliberately only `ready`, though the state machine also permits a stop out
 * of `provisioning`. Provisioning runs inline in another request: stopping
 * mid-boot settles the session at `stopped` and then that request finishes and
 * starts a container for it, leaving one nothing will reclaim. Cancelling a
 * boot needs the provisioner to cooperate, which is more than a button.
 *
 * `running` is excluded for a different reason — the server refuses to stop
 * under a live turn, so offering it here would only produce a toast.
 */
export function acceptsStop(state: CodeSessionState): boolean {
  return state === "ready"
}

/**
 * True when the commit controls should accept a click.
 *
 * Narrower than `isSandboxLive` on purpose: the sandbox answers during a turn,
 * but committing then would stage files the agent is still writing, and the
 * server refuses it. Disabling the buttons says so before the round trip.
 */
export function acceptsCommit(state: CodeSessionState): boolean {
  return state === "ready"
}

export interface CommitCodeInput {
  subject: string
  body?: string
  /** True for "Commit & sync"; the working branch is pushed after committing. */
  push?: boolean
}

export interface CommitCodeResult {
  commitSha: string
  branch: string
  filesChanged: number
  pushed: boolean
  /**
   * Why the push did not happen, when one was asked for.
   *
   * Separate from a failed request because the commit still landed — reporting
   * the whole action as failed is what makes a user commit the same work twice.
   */
  pushError: string | null
}

/**
 * Commits everything the diff pane is showing.
 *
 * One call for both buttons: `push` folds "Commit & sync" in, so a rejected
 * push cannot leave behind a commit the UI never mentioned.
 */
export async function commitCodeSession(
  sessionId: string,
  input: CommitCodeInput,
): Promise<CommitCodeResult> {
  const response = (await api.post(`/code/sessions/${sessionId}/commit`, input)) as {
    data: CommitCodeResult
  }
  return response.data
}

/** Throws away uncommitted work. Commits already made are left alone. */
export async function discardCodeChanges(
  sessionId: string,
): Promise<{ filesDiscarded: number }> {
  const response = (await api.post(`/code/sessions/${sessionId}/discard`, {})) as {
    data: { filesDiscarded: number }
  }
  return response.data
}

export interface GeneratedCommitMessage {
  subject: string
  body: string
  /** True when a local heuristic wrote it because the model was unavailable. */
  fallback: boolean
}

/**
 * Drafts a commit message from the session's current diff.
 *
 * The server reads the diff itself rather than taking ours, so the message
 * describes what is in the sandbox now and not the last poll's snapshot.
 */
export async function generateCommitMessage(
  sessionId: string,
): Promise<GeneratedCommitMessage> {
  const response = (await api.post(`/code/sessions/${sessionId}/commit-message`, {})) as {
    data: GeneratedCommitMessage
  }
  return response.data
}

/** One GitHub account this workspace has installed the App on. */
export interface GithubInstallation {
  id: string
  installationId: string
  accountLogin: string
  accountType: string
  repoSelection: "all" | "selected"
  /** GitHub's own page for changing which repositories are shared. */
  manageUrl: string
}

/** Whether coding can reach any repositories, and on whose behalf. */
export interface GithubConnectionStatus {
  /**
   * False when the deployment has no GitHub App registered at all. An operator
   * problem, not something the user can fix by clicking anything — the UI says
   * so rather than offering a button that would 404 on GitHub.
   */
  configured: boolean
  /**
   * The App's handle, without the `@`. Null when unconfigured.
   *
   * Needed by the automations screen: it exists to tell somebody what to type in
   * a GitHub comment, and the slug is server-side configuration it has no other
   * way to learn.
   */
  slug: string | null
  installations: GithubInstallation[]
}

/** A repository the installation may act on. */
export interface GithubRepo {
  owner: string
  name: string
  fullName: string
  defaultBranch: string
  private: boolean
}

export async function getGithubConnectionStatus(): Promise<GithubConnectionStatus> {
  // The connect screen explains a failure in place; a toast on top of it just
  // says the same thing twice, in the language of the API rather than the user's.
  const response = (await api.get("/code/github/status", { showErrorToast: false })) as {
    data: GithubConnectionStatus
  }
  return response.data
}

/**
 * Mints the URL that starts an installation.
 *
 * Fetched rather than linked to because the client authenticates with a bearer
 * token: a plain link would arrive unauthenticated and could not know which
 * workspace was asking. The caller navigates to what comes back.
 */
export async function getGithubInstallUrl(appId?: string): Promise<string> {
  const path =
    appId === undefined
      ? "/code/github/install-url"
      : `/code/github/install-url?appId=${encodeURIComponent(appId)}`
  const response = (await api.get(path)) as { data: { url: string } }
  return response.data.url
}

/**
 * Repositories the workspace's installations cover.
 *
 * Read live from GitHub on every call, so a repository the customer removed
 * stops being offered immediately rather than failing at clone time.
 */
export async function listGithubRepos(): Promise<GithubRepo[]> {
  // Same reasoning as the status probe: the picker says so itself, and offers
  // the retry.
  const response = (await api.get("/code/github/repos", { showErrorToast: false })) as {
    data?: GithubRepo[]
  }
  return response.data ?? []
}

/** An open pull request in a repository this workspace has sessions in. */
export interface CodePullRequest {
  number: number
  title: string
  repoOwner: string
  repoName: string
  /** Branch the PR merges FROM — what a review session checks out. */
  headBranch: string
  baseBranch: string
  authorLogin: string | null
  htmlUrl: string
  additions: number | null
  deletions: number | null
  updatedAt: string
  draft: boolean
}

export interface CodePullRequestList {
  pullRequests: CodePullRequest[]
  /**
   * How many repositories were searched.
   *
   * Returned so the empty state can say how wide the search was — "no open pull
   * requests" reads as a broken integration without it.
   */
  repositoryCount: number
  /** True when the installation covers more repositories than were searched. */
  truncated: boolean
  /**
   * False when GitHub would not issue a token.
   *
   * Separate from an empty list because they look identical and mean opposite
   * things: a stored installation id goes stale the moment the App is
   * reinstalled, and reporting that as "no pull requests" sends the reader
   * looking for the wrong problem.
   */
  reachable: boolean
  /** False when no GitHub App installation exists yet. */
  connected: boolean
}

/** Open pull requests, read live from GitHub. */
export async function listCodePullRequests(appId?: string): Promise<CodePullRequestList> {
  const path =
    appId === undefined
      ? "/code/pull-requests"
      : `/code/pull-requests?appId=${encodeURIComponent(appId)}`
  const response = (await api.get(path)) as { data?: CodePullRequestList }
  return response.data ?? {
    pullRequests: [],
    repositoryCount: 0,
    truncated: false,
    connected: false,
    reachable: false,
  }
}
